import { call } from '@rental/contracts/client'
import { FUEL_TYPES, type FuelType, TRANSMISSIONS, type Transmission } from '@rental/domain'
import { format } from '@rental/i18n'
import { Button, Checkbox, Field } from '@rental/ui'
import { type FormEvent, type RefObject, useLayoutEffect, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { Dialog } from '../components/Dialog'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import {
  type CategoryGroup,
  type Linkable,
  type ModelMap,
  modelLabel,
  nextPosition,
  type StaffModel,
  type StaffModelCategory,
  writeBack,
} from './models'
import { type ChosenPhoto, ModelPictureOf, PhotoField, PictureOf } from './parts'
import { uploadModelPhoto } from './upload'

/** A category's name in the app's language; the Italian, or the code, when it has none. */
export function categoryName(category: StaffModelCategory, locale: string): string {
  return category.name[locale] ?? category.name.it ?? category.code
}

/** Opens a dialog once it is handed something to show (the Prices dialogs' way). */
function useOpen(dialogRef: RefObject<HTMLDialogElement | null>, open: boolean) {
  useLayoutEffect(() => {
    if (open && !dialogRef.current?.open) dialogRef.current?.showModal()
  }, [open, dialogRef])
}

type Busy = 'sending' | 'saving' | null

/** The specs of a model added here, as typed: empty is "not given". */
interface Specs {
  seats: string
  transmission: Transmission | ''
  fuel: FuelType | ''
}

const specsOf = (model: StaffModel | null): Specs => ({
  seats: model?.seats ? String(model.seats) : '',
  transmission: model?.transmission ?? '',
  fuel: model?.fuel ?? '',
})

/** Seats 1–9, or nothing; anything else is not a number of seats. */
function seatsOf(text: string): { ok: true; value: number | null } | { ok: false } {
  if (text.trim() === '') return { ok: true, value: null }
  const value = Number(text)
  return Number.isInteger(value) && value >= 1 && value <= 9 ? { ok: true, value } : { ok: false }
}

function SpecFields({ specs, onChange }: { specs: Specs; onChange: (specs: Specs) => void }) {
  const { messages: m } = useLocale()
  const words = m.models
  const seats = seatsOf(specs.seats)
  return (
    <fieldset className="flex min-w-0 flex-col gap-3">
      <legend className="sr-only">{words.specsHint}</legend>
      <p className="text-sm text-text-muted">{words.specsHint}</p>
      <div className={FIELD_ROW}>
        <Field className={FIELD} label={words.seats} error={seats.ok ? null : words.seatsInvalid}>
          {(control) => (
            <StaffInput
              {...control}
              inputMode="numeric"
              value={specs.seats}
              onChange={(event) => onChange({ ...specs, seats: event.target.value })}
            />
          )}
        </Field>
        <Field className={FIELD} label={words.transmission}>
          {(control) => (
            <StaffSelect
              {...control}
              value={specs.transmission}
              onChange={(event) =>
                onChange({ ...specs, transmission: event.target.value as Specs['transmission'] })
              }
            >
              <option value="">{words.notGiven}</option>
              {TRANSMISSIONS.map((value) => (
                <option key={value} value={value}>
                  {m.transmissions[value]}
                </option>
              ))}
            </StaffSelect>
          )}
        </Field>
        <Field className={FIELD} label={words.fuel}>
          {(control) => (
            <StaffSelect
              {...control}
              value={specs.fuel}
              onChange={(event) =>
                onChange({ ...specs, fuel: event.target.value as Specs['fuel'] })
              }
            >
              <option value="">{words.notGiven}</option>
              {FUEL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {m.fuels[value]}
                </option>
              ))}
            </StaffSelect>
          )}
        </Field>
      </div>
    </fieldset>
  )
}

function CategorySelect({
  value,
  map,
  onChange,
}: {
  value: string
  map: ModelMap
  onChange: (categoryId: string) => void
}) {
  const { locale, messages: m } = useLocale()
  return (
    <Field label={m.models.category}>
      {(control) => (
        <StaffSelect {...control} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{m.models.noCategory}</option>
          {map.groups.map(({ category }) => (
            <option key={category.id} value={category.id}>
              {categoryName(category, locale)}
              {category.active ? '' : ` (${m.models.inactiveCategory})`}
            </option>
          ))}
        </StaffSelect>
      )}
    </Field>
  )
}

const groupOf = (map: ModelMap, categoryId: string | null) =>
  map.groups.find((group) => group.category.id === categoryId)

/**
 * After a model joins a category: last in its order, and its lead if the
 * category had none. Linked anyway if this second write is refused.
 */
async function settleIn(model: StaffModel, group: CategoryGroup | undefined) {
  if (!group) return
  await call(
    api.PUT('/v1/staff/models/{id}', {
      params: { path: { id: model.id } },
      body: writeBack(model, { position: nextPosition(group), isLead: group.lead === undefined }),
    }),
  )
}

/**
 * One model written whole with the version read: its category (none to
 * unlink), whether it leads it, its photo; a model added here also its name,
 * specs and whether it is in use. A website model's name and specs are the
 * website's, and are not offered.
 */
export function EditDialog({
  dialogRef,
  model,
  map,
  onSaved,
  onClose,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  model: StaffModel | null
  map: ModelMap
  onSaved: (text: string) => void
  /** Every way out: the screen lets go of the model, so the next opening starts afresh. */
  onClose: () => void
}) {
  const { messages: m } = useLocale()
  const words = m.models
  const { takeOver } = useAccess()
  const [opened, setOpened] = useState<StaffModel | null>(null)
  const [categoryId, setCategoryId] = useState('')
  const [isLead, setIsLead] = useState(false)
  const [active, setActive] = useState(true)
  const [name, setName] = useState('')
  const [specs, setSpecs] = useState<Specs>(specsOf(null))
  const [photo, setPhoto] = useState<ChosenPhoto>(null)
  const [sitePhoto, setSitePhoto] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState<Busy>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  if (model !== opened) {
    setOpened(model)
    setCategoryId(model?.categoryId ?? '')
    setIsLead(model?.isLead ?? false)
    setActive(model?.active ?? true)
    setName(model?.name ?? '')
    setSpecs(specsOf(model))
    setPhoto(null)
    setSitePhoto(false)
    setPhotoError(null)
    setTried(false)
    setProblem(null)
  }
  useOpen(dialogRef, model !== null)

  if (!model)
    return (
      <Dialog dialogRef={dialogRef} title="">
        {null}
      </Dialog>
    )
  const own = model.siteRef === null
  const target = groupOf(map, categoryId || null)
  const leadNow = target?.lead && target.lead.id !== model.id ? target.lead : undefined
  const seats = seatsOf(specs.seats)
  const nameMissing = own && name.trim() === ''

  async function save() {
    if (!model || busy) return
    setTried(true)
    if (nameMissing || (own && !seats.ok)) return
    setProblem(null)
    let photoMediaId = sitePhoto ? null : model.photoMediaId
    if (photo) {
      setBusy('sending')
      const sent = await uploadModelPhoto(photo.blob)
      if (!sent.ok) {
        setBusy(null)
        if (!takeOver(sent.problem)) setProblem(sent.problem)
        return
      }
      photoMediaId = sent.mediaId
    }
    setBusy('saving')
    const moved = (categoryId || null) !== model.categoryId
    const result = await call(
      api.PUT('/v1/staff/models/{id}', {
        params: { path: { id: model.id } },
        body: writeBack(model, {
          categoryId: active ? categoryId || null : null,
          isLead: active && categoryId !== '' && isLead,
          position: moved ? nextPosition(target) : model.position,
          photoMediaId,
          active,
          ...(own
            ? {
                name: name.trim(),
                seats: seats.ok ? seats.value : null,
                transmission: specs.transmission || null,
                fuel: specs.fuel || null,
              }
            : {}),
        }),
      }),
    )
    setBusy(null)
    if (!result.ok) {
      if (!takeOver(result.problem)) setProblem(result.problem)
      return
    }
    dialogRef.current?.close()
    onSaved(format(words.saved, { model: modelLabel(result.data, m.transmissions) }))
  }

  return (
    <Dialog
      dialogRef={dialogRef}
      title={format(words.editTitle, { model: modelLabel(model, m.transmissions) })}
      busy={busy !== null}
      onClose={onClose}
      className="max-w-lg"
    >
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void save()
        }}
      >
        {own ? (
          <Field label={words.name} error={tried && nameMissing ? words.nameRequired : null}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={60}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
        ) : null}
        {active ? (
          <CategorySelect
            value={categoryId}
            map={map}
            onChange={(next) => {
              setCategoryId(next)
              if (next === '') setIsLead(false)
            }}
          />
        ) : null}
        {active && categoryId !== '' ? (
          <div>
            <Checkbox
              label={words.isLead}
              checked={isLead}
              onChange={(event) => setIsLead(event.target.checked)}
            />
            {isLead && leadNow ? (
              <p className="text-sm text-text-muted">
                {format(words.leadTaken, { model: modelLabel(leadNow, m.transmissions) })}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          <p className="font-medium text-sm">{words.photo}</p>
          <div className="max-w-48">
            <ModelPictureOf model={sitePhoto ? { ...model, photoMediaId: null } : model} />
          </div>
          {!own && model.photoMediaId ? (
            <Checkbox
              label={words.sitePhoto}
              checked={sitePhoto}
              onChange={(event) => {
                setSitePhoto(event.target.checked)
                if (event.target.checked) setPhoto(null)
              }}
            />
          ) : null}
          {sitePhoto ? null : (
            <PhotoField
              ref={photoInput}
              label={words.newPhoto}
              chosen={photo}
              error={photoError}
              onChosen={(next) => {
                setPhotoError(null)
                setPhoto(next)
              }}
              onUnreadable={() => setPhotoError(words.unreadable)}
            />
          )}
        </div>
        {own ? <SpecFields specs={specs} onChange={setSpecs} /> : null}
        {own ? (
          <div>
            <Checkbox
              label={words.active}
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            <p className="text-sm text-text-muted">{words.retireHint}</p>
          </div>
        ) : null}
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => dialogRef.current?.close()}>
            {words.cancel}
          </Button>
          <Button type="submit" variant="staff" busy={busy !== null}>
            {busy === 'sending' ? words.sending : busy === 'saving' ? words.saving : words.save}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/** The website's models free to link, as pictures: one press links it here. */
export function LinkDialog({
  dialogRef,
  group,
  choices,
  onLinked,
  onClose,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  group: CategoryGroup | null
  choices: readonly Linkable[]
  onLinked: (text: string) => void
  onClose: () => void
}) {
  const { locale, messages: m } = useLocale()
  const words = m.models
  const { takeOver } = useAccess()
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [opened, setOpened] = useState<CategoryGroup | null>(null)
  if (group !== opened) {
    setOpened(group)
    setProblem(null)
  }
  useOpen(dialogRef, group !== null)
  if (!group)
    return (
      <Dialog dialogRef={dialogRef} title="">
        {null}
      </Dialog>
    )
  const category = categoryName(group.category, locale)
  const labelOf = (choice: Linkable) =>
    modelLabel({ siteRef: choice.siteRef, name: null }, m.transmissions)

  async function link(choice: Linkable) {
    if (!group || busy) return
    setProblem(null)
    setBusy(choice.siteRef)
    const result = choice.model
      ? await call(
          api.PUT('/v1/staff/models/{id}', {
            params: { path: { id: choice.model.id } },
            body: writeBack(choice.model, {
              categoryId: group.category.id,
              position: nextPosition(group),
              isLead: group.lead === undefined,
            }),
          }),
        )
      : await call(
          api.POST('/v1/staff/models', {
            body: { siteRef: choice.siteRef, categoryId: group.category.id },
          }),
        )
    if (result.ok && !choice.model) await settleIn(result.data, group)
    setBusy(null)
    if (!result.ok) {
      if (!takeOver(result.problem)) setProblem(result.problem)
      return
    }
    dialogRef.current?.close()
    onLinked(format(words.linked, { model: labelOf(choice), category }))
  }

  return (
    <Dialog
      dialogRef={dialogRef}
      title={format(words.linkTitle, { category })}
      busy={busy !== null}
      onClose={onClose}
      className="max-w-3xl"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-muted">
          {choices.length > 0 ? words.linkHint : words.linkNone}
        </p>
        <ProblemNotice problem={problem} />
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {choices.map((choice) => (
            <li key={choice.siteRef}>
              <button
                type="button"
                className="flex w-full flex-col gap-2 rounded-sm p-2 text-start hover:bg-surface-well disabled:opacity-60"
                disabled={busy !== null}
                aria-busy={busy === choice.siteRef}
                onClick={() => void link(choice)}
              >
                {/* The button is named by the model's name under it; its picture adds nothing to say. */}
                <PictureOf picture={{ kind: 'site', src: choice.photo }} alt="" />
                <span className="font-medium">{labelOf(choice)}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={() => dialogRef.current?.close()}>
            {words.cancel}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * A model the website lacks: its name and photo, which are required, and the
 * specs that are known. It has no page on the website; the form says so, and
 * that the photo will be on it.
 */
export function AddDialog({
  dialogRef,
  opened: openCount,
  map,
  onAdded,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  opened: number
  map: ModelMap
  onAdded: (text: string) => void
}) {
  const { messages: m } = useLocale()
  const words = m.models
  const { takeOver } = useAccess()
  const [seen, setSeen] = useState(0)
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [specs, setSpecs] = useState<Specs>(specsOf(null))
  const [photo, setPhoto] = useState<ChosenPhoto>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState<Busy>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  if (openCount !== seen) {
    setSeen(openCount)
    setName('')
    setCategoryId('')
    setSpecs(specsOf(null))
    setPhoto(null)
    setPhotoError(null)
    setTried(false)
    setProblem(null)
  }
  useLayoutEffect(() => {
    if (openCount > 0 && !dialogRef.current?.open) dialogRef.current?.showModal()
  }, [openCount, dialogRef])
  const seats = seatsOf(specs.seats)
  const nameMissing = name.trim() === ''

  async function save() {
    if (busy) return
    setTried(true)
    if (!photo) setPhotoError(words.photoRequired)
    if (nameMissing || !photo || !seats.ok) {
      if (!photo) photoInput.current?.focus()
      return
    }
    setProblem(null)
    setBusy('sending')
    const sent = await uploadModelPhoto(photo.blob)
    if (!sent.ok) {
      setBusy(null)
      if (!takeOver(sent.problem)) setProblem(sent.problem)
      return
    }
    setBusy('saving')
    const result = await call(
      api.POST('/v1/staff/models', {
        body: {
          name: name.trim(),
          photoMediaId: sent.mediaId,
          categoryId: categoryId || null,
          seats: seats.ok ? seats.value : null,
          transmission: specs.transmission || null,
          fuel: specs.fuel || null,
        },
      }),
    )
    if (result.ok) await settleIn(result.data, groupOf(map, categoryId || null))
    setBusy(null)
    if (!result.ok) {
      if (!takeOver(result.problem)) setProblem(result.problem)
      return
    }
    dialogRef.current?.close()
    onAdded(format(words.added, { model: name.trim() }))
  }

  return (
    <Dialog dialogRef={dialogRef} title={words.addTitle} busy={busy !== null} className="max-w-lg">
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void save()
        }}
      >
        <p className="text-sm text-text-muted">{words.addHint}</p>
        <Field label={words.name} error={tried && nameMissing ? words.nameRequired : null}>
          {(control) => (
            <StaffInput
              {...control}
              maxLength={60}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <PhotoField
          ref={photoInput}
          label={words.photo}
          chosen={photo}
          error={photoError}
          onChosen={(next) => {
            setPhotoError(null)
            setPhoto(next)
          }}
          onUnreadable={() => setPhotoError(words.unreadable)}
        />
        <CategorySelect value={categoryId} map={map} onChange={setCategoryId} />
        <SpecFields specs={specs} onChange={setSpecs} />
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => dialogRef.current?.close()}>
            {words.cancel}
          </Button>
          <Button type="submit" variant="staff" busy={busy !== null}>
            {busy === 'sending' ? words.sending : busy === 'saving' ? words.saving : words.add}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
